/**
 * Config value calculation utilities for service detail
 */
import type { ServiceDetailRow } from './types'
import { hasDisplayableValue } from '../expanded-row'
import { getConfigQuota } from '../templates'

/**
 * Config name aliases - map primary name to all possible variations
 */
const CONFIG_NAME_ALIASES: Record<string, string[]> = {
    ip: ['ip', 'ip-address', 'ipv4', 'ipv6'],
    bandwidth: ['bandwidth', 'bw'],
    memory: ['memory', 'ram'],
    disk: ['disk', 'ssd', 'hdd', 'storage'],
    cpu: ['cpu', 'vcore', 'vcpu'],
    power: ['power', 'nguon'],
    'peak-bandwidth': ['peak-bandwidth', 'peak_bandwidth', 'peak-bw'],
}

/**
 * Get all name variations for a config name
 */
const getConfigNameVariations = (configName: string): string[] => {
    const lowered = configName.toLowerCase()
    return CONFIG_NAME_ALIASES[lowered] || [lowered]
}

/**
 * Check if config name matches any variation
 */
const matchesConfigName = (
    actualName: string | undefined,
    targetName: string
): boolean => {
    if (!actualName) return false
    const lowered = actualName.toLowerCase()
    const variations = getConfigNameVariations(targetName)
    return variations.includes(lowered)
}

/**
 * Sum config values from configoptions array
 * @param configoptions - Array of config objects
 * @param configName - Name of config to sum (case-insensitive, supports aliases)
 * @param useFormula - Use capacity × commitment formula (for VBM/Colocation)
 * @param serviceTemplate - Template for formula detection
 */
export const sumConfigValue = (
    configoptions: any[],
    configName: string,
    useFormula = true,
    serviceTemplate?: string
): number => {
    const configs =
        configoptions?.filter((c: any) =>
            matchesConfigName(c?.name, configName)
        ) || []

    return configs.reduce((total: number, config: any) => {
        if (useFormula) {
            return total + getConfigQuota(config, serviceTemplate)
        }
        return total + (config?.commitment_value || 0)
    }, 0)
}

/**
 * Check if any row has a specific config with displayable value
 */
export const hasConfigInRows = (
    rows: ServiceDetailRow[],
    configName: string,
    serviceTemplate?: string
): boolean => {
    return rows.some((row) =>
        row?.configoptions?.some((c: any) => {
            if (!matchesConfigName(c?.name, configName)) return false
            return hasDisplayableValue(c, serviceTemplate)
        })
    )
}

/**
 * Get processor/server model name from configoptions
 */
export const getProcessorModel = (configoptions: any[]): string | undefined => {
    const config = configoptions?.find((c: any) => {
        const name = c?.name?.toLowerCase()
        return name === 'processor' || name === 'server'
    })
    return config?.model_name
}

/**
 * Get CPU capacity value from configoptions
 */
export const getCpuCapacity = (configoptions: any[]): number => {
    const config = configoptions?.find((c: any) => {
        const name = c?.name?.toLowerCase()
        return (
            name === 'processor' ||
            name === 'server' ||
            name === 'cpu' ||
            name === 'vcore'
        )
    })
    return config?.capacity_value || 0
}
